import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { Store } from "@ngrx/store";
import { UserMe } from "@serasa/core/modules/recertification/interfaces/user-me.interface";
import { Observable } from "rxjs";
import { selectUserMeInfo } from "../../core/modules/users/state/selectors/user.selectors";
import { UserChannelCode } from "../enums/user-channel-code.enum";
import { CustomErrorInterface } from "../utils/interfaces/custom-error.interface";
import {
  PageLoadInterface,
  UserCustomInterface,
  UserInfoInterface,
} from "../utils/interfaces/page-load.interface";
import { Utils } from "../utils/utils";

interface CustomDocument extends Document {
  DataLayer?: any;
}

declare let document: CustomDocument;
interface SatelliteWindow extends Window {
  _satellite?: any;
}

declare let window: SatelliteWindow;

type DataLayer = PageLoadInterface | CustomErrorInterface;

@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  private readonly ENV = "LG";
  private readonly APP_NAME = "PortalDeLogons";
  public userChannel!: UserMe;
  public userIsPME = false;
  public userData$?: Observable<UserMe | null | undefined>;
  public cnpjBase$?: string;

  constructor(private store: Store) {
    this.userData$ = this.store.select(selectUserMeInfo);

    this.userData$?.subscribe((userData) => {
      if (userData) {
        this._verifyIfUserChannelIsPMEAndCnpjBase(userData);
      }
    });
  }

  public doPageLoad(
    page: string,
    customObj?: UserCustomInterface,
    customEventName?: string,
  ): void {
    const pageName = `MP:${this.ENV}:IN:${this.APP_NAME}:${page}`;

    const dataLayer: PageLoadInterface = {
      pageInfo: {
        pageName,
        url: Utils.locationHref(),
        siteSection: this.APP_NAME,
        ambiente: this.ENV,
      },
      userInfo: this._getUserInfo(),
      rule: "pageLoad",
    };

    if (customObj) {
      dataLayer.custom = {
        ...customObj,
      };
    }

    this._createAndTrackDataLayer(
      dataLayer,
      customEventName || "CustomPageView",
    );
  }

  public doCustomEvent(
    customObject: UserCustomInterface,
    customEventName: string,
  ): void {
    const customDataLayer: PageLoadInterface = {
      custom: customObject,
      userInfo: this._getUserInfo(),
      rule: "pageLoad",
    };

    this._createAndTrackDataLayer(customDataLayer, customEventName);
  }

  public sendCustomError(
    httpError: HttpErrorResponse,
    errorMessage: string,
    method = "GET",
  ): void {
    const status = httpError?.status ? httpError.status : "CONNECTIVITY";
    const url = new URL(httpError.url as string);
    const { protocol, pathname } = url;

    const customError: CustomErrorInterface = {
      custom: {
        events: ["error"],
        errorDetail: `${protocol?.toUpperCase()}${status}:${method.toUpperCase()}/${pathname}`,
        errorMessage,
        customLink: "customError",
      },
    };

    this._createAndTrackDataLayer(customError, "CustomError");
  }

  private _getUserInfo(): UserInfoInterface {
    const decodedToken = Utils.decodeToken();
    const currentDateTime = new Date().toISOString();

    const userInfo: UserInfoInterface = {
      racf: decodedToken?.logon,
      authenticationMethod: "IAM",
      authenticationChannel: "Institucional",
      businessId: decodedToken?.business_unit_id,
      clientId: decodedToken?.client_id,
      userId: decodedToken?.user_id,
      canal: this.userIsPME === true ? "PME" : "LARGE",
      profile: Utils.verifyUserProfile(),
      executionDateTime: currentDateTime,
    };

    if (this.cnpjBase$) {
      userInfo['cnpjBase'] = this.cnpjBase$
    }

    return userInfo;
  }

  private _createAndTrackDataLayer(
    dataLayer: DataLayer,
    trackName: string,
  ): void {
    if (window["_satellite"]) {
      document["DataLayer"] = dataLayer;
      document.dispatchEvent(
        new CustomEvent(trackName, { detail: document.DataLayer }),
      );
    }
  }

  private _verifyIfUserChannelIsPMEAndCnpjBase(userChannel: UserMe): void {
    this.userChannel = userChannel;

    if (userChannel?.company) {
      this.cnpjBase$ = userChannel?.company?.companyDocument;
    }

    if (userChannel?.channel?.channelCode !== undefined) {
      const channelCode = userChannel.channel.channelCode;
      const isPME =
        channelCode === UserChannelCode.SEM_ATRIBUICAO ||
        channelCode === UserChannelCode.DISTRIBUIDOR ||
        channelCode === UserChannelCode.PME ||
        channelCode === UserChannelCode.CONVENIO_SPC_SERASA;
      this.userIsPME = isPME;
    } else {
      this.userIsPME = false;
    }
  }

}
